import {Injectable} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {GefConnectionDto} from "../../dtos/gef/gef-connection-dto";
import {Auth2Service} from "../security/auth2.service";
import {HttpService} from "../technique/http.service";
import {ResponseWrapper} from "../../suppliers/wrappers/response-wrapper";
import {GraphQLService} from "../technique/graphql.service";
import {ObjectDTO} from "../../dtos/object-dto";
import {GefAuditDto} from "../../dtos/gef/gef-audit-dto";
import {DolFlowOrchestratorConfigDto} from "../../dtos/gef/dol-flow-orchestrator-config-dto";
import {DolFlowOrchestratorFluxDto} from "../../dtos/gef/dol-flow-orchestrator-flux-dto";

@Injectable({
  providedIn: 'root'
})
export class GestionGefService {

  URL_FLUX_GEF_EXPORT_FOURNISSEUR: String = `dolrest/jobs/export-gef-fournisseur`;
  URL_FLUX_GEF_EXPORT_FPRODUIT_DECLINAISON: String = `dolrest/jobs/export-gef-produit-declinaison`;
  URL_FLUX_GEF_EXPORT_COMMANDE_FOURNISSEUR: String = `dolrest/jobs/export-gef-bon-commande-fournisseur`;
  URL_FLUX_GEF_EXPORT_BON_RECEPTION: String = `dolrest/jobs/export-gef-bon-reception`;

  private gefConnectionFluxEditionDialog = new Subject<GefConnectionDto>();
  openGefConnectionFluxEditionDialog = this.gefConnectionFluxEditionDialog.asObservable();

  private subjectGefConnectionSaved = new Subject<GefConnectionSavedSupplier>();
  gefConnectionSaved$ = this.subjectGefConnectionSaved.asObservable();

  private subjectOpenDuplicationDialog = new Subject<GefDuplicationDialogSupplier>();
  openDuplicationDialog$ = this.subjectOpenDuplicationDialog.asObservable();

  private subjectDisplayGefAudit = new Subject<GefAuditDto[]>();
  displayGefAudit$ = this.subjectDisplayGefAudit.asObservable();

  private subjectOpenModificationMasseDialog = new Subject<GefConnectionDto[]>();
  openModificationMasseDialog$ = this.subjectOpenModificationMasseDialog.asObservable();

  private subjectRefreshGefConnections = new Subject<void>();
  refreshGefConnections$ = this.subjectRefreshGefConnections.asObservable();

  announceOpenGefConnectionFluxEditionDialog = (connection: GefConnectionDto) => {
    this.gefConnectionFluxEditionDialog.next(connection);
  };

  announceOpenDuplicationDialog = (connection: GefConnectionDto, existingConnections: GefConnectionDto[]) => {
    this.subjectOpenDuplicationDialog.next({connection, existingConnections});
  };

  announceGefConnectionSaved = (connection: GefConnectionDto, isUpdate: boolean = false) => {
    this.subjectGefConnectionSaved.next({isUpdate, gefConnection: connection} as GefConnectionSavedSupplier);
  };

  announceOpenGefAuditDialog = (gefAuditList: GefAuditDto[]) => {
    this.subjectDisplayGefAudit.next(gefAuditList);
  };

  announceOpenModificationMasseDialog = (connectionList: GefConnectionDto[]) => {
    this.subjectOpenModificationMasseDialog.next(connectionList);
  };

  announceRefreshGefConnections = () => {
    this.subjectRefreshGefConnections.next();
  };


  constructor(private auth2Svc: Auth2Service,
              private httpSvc: HttpService,
              private graphQlSvc: GraphQLService
  ) {
  }

  findAllGefConnections = (): Observable<ResponseWrapper<GefConnectionDto>> => {
    return this.httpSvc.get('dolrest/gef/connection', null);
  }

  findAllGefHistory = (): Observable<any> => {
    return this.graphQlSvc.sendQuery(`
        {
          allGefHistory{
              id,
              operation,
              description,
              args,
              dateEvent,
              loginUser
          }
        }
      `)
  }

  findAllGefProviders = (): Observable<any> => {
    return this.graphQlSvc.sendQuery(`
        {
          allGefProviders {
              id,
              libelle,
              code,
              actif
          }
        }
      `)
  }

  findAllBonCfStatus(ordre: string): Observable<any> {
    return this.graphQlSvc.sendQuery(`
        {
          allBonCfStatus(filters: {
                ordre: "ASC"
              }) {
              id,
              libelle,
              code
          }
        }
      `)
  }

  findAllGefDenreeDeclineeDeclinaisonDefaultValues(idSite: number): Observable<any> {
    return this.graphQlSvc.sendQuery(`
        {
          allGefDenreeDeclineeDeclinaisonDefaultValues(filters: {
                idSite: ${idSite}
              })
        }
      `)
  }

  launchFluxExportFournisseurs(importPartiel: boolean): Observable<any> {
    return this.httpSvc.get(`${this.URL_FLUX_GEF_EXPORT_FOURNISSEUR}?importPartiel=${importPartiel}`, null);
  }

  launchFluxExportArticles(importPartiel: boolean): Observable<any> {
    return this.httpSvc.get(`${this.URL_FLUX_GEF_EXPORT_FPRODUIT_DECLINAISON}?importPartiel=${importPartiel}`, null);
  }

  launchFluxExportCommandesFournisseurs(importPartiel: boolean): Observable<any> {
    return this.httpSvc.get(`${this.URL_FLUX_GEF_EXPORT_COMMANDE_FOURNISSEUR}?importPartiel=${importPartiel}`, null);
  }

  launchFluxExportBonsReception(importPartiel: boolean): Observable<any> {
    return this.httpSvc.get(`${this.URL_FLUX_GEF_EXPORT_BON_RECEPTION}?importPartiel=${importPartiel}`, null);
  }

  getDolFlowOrchestratorSwagger(): Observable<ResponseWrapper<DolFlowOrchestratorFluxDto>> {
    return this.httpSvc.get('dolrest/gef/dol-flow-orchestrator/swagger', null);
  }

  triggerDolFlowOrchestratorFlux(flux: DolFlowOrchestratorFluxDto): Observable<ResponseWrapper<any>> {
    return this.httpSvc.post('dolrest/gef/dol-flow-orchestrator/trigger', flux);
  }

  getDolFlowOrchestratorConfig(): Observable<ResponseWrapper<DolFlowOrchestratorConfigDto>> {
    return this.httpSvc.get('dolrest/gef/dol-flow-orchestrator/config', null);
  }

  saveDolFlowOrchestratorConfig(config: DolFlowOrchestratorConfigDto): Observable<ResponseWrapper<DolFlowOrchestratorConfigDto>> {
    return this.httpSvc.post('dolrest/gef/dol-flow-orchestrator/config', config);
  }

  modificationMasseGefConnectionFlux = (gefConnectionIdList: number[], gefFluxList: string[]): Observable<void> => {
    return this.httpSvc.post('dolrest/gef/connection/modification-masse-flux', {gefConnectionIdList, gefFluxList});
  }

  save = (gefConnection: GefConnectionDto): Observable<ResponseWrapper<GefConnectionDto>> => {
    return this.httpSvc.post('dolrest/gef/save', gefConnection);
  }

  dupliquerGefConnection = (model: GefConnectionDto, siteIdList: number[]): Observable<ResponseWrapper<GefConnectionDto>> => {
    const payload = {...model, jsonProperties: model.properties};
    return this.httpSvc.post('dolrest/gef/connection/dupliquer', {model: payload, siteIdList});
  }

  canModify(element: ObjectDTO): boolean {
    if (!element || !element?.site || !element?.site.id)
      return true;
    return this.auth2Svc.isSiteLocal(element.site.id);
  }

  updateGefAudit(gefAudit: GefAuditDto): Observable<any> {
    return this.httpSvc.put(`dolrest/gef/entityAudit`, gefAudit);
  }


  findGefAuditList(entityName: string, idEntityList: number[]): Observable<any> {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
        {
          allGefAuditList(filters: {
            siteIds: [${idsSites}],
            idsEntity: [${idEntityList}],
            entityName: "${entityName}"
          }) {
              id,
              idEntity,
              entityName,
              gefConnection {
                gefProvider {
                  libelle
                }
              }
              flux,
              lastSynchro,
              validationRequired,
              description
          }
        }
      `)
  }

  retrieveGefAuditListByIdEntity(gefAuditList: GefAuditDto[], idEntity: number): GefAuditDto[] {
    return gefAuditList.filter(gefAudit => gefAudit.idEntity === idEntity);
  }


  findFournisseurToValidate(): Observable<any> {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
        {
          allFournisseurToValidateGef(filters: {
            siteIds: [${idsSites}]
          }) {
              id,
              libelle,
              site {
                id,
                libelle
              }
          }
        }
      `)
  }

  findProduitToValidate(): Observable<any> {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
        {
          allProduitToValidateGef(filters: {
            siteIds: [${idsSites}]
          }) {
              id,
              libelle,
              site {
                id,
                libelle
              }
          }
        }
      `)
  }

  findProduitDeclinaisonToValidate(): Observable<any> {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
        {
          allProduitDeclinaisonToValidateGef(filters: {
            siteIds: [${idsSites}]
          }) {
              id,
              libelle,
              produitId,
              site {
                id,
                libelle
              }
          }
        }
      `)
  }

  findCatalogueAchatToValidate(): Observable<any> {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
        {
          allCatalogueAchatToValidateGef(filters: {
            idsSites: [${idsSites}]
          }) {
              id,
              produitArticle {
                id,
                libelle
              },
              produitDeclinaison {
                produitId
              },
              site {
                id,
                libelle
              }
          }
        }
      `)
  }

  findMarcheToValidate(): Observable<any> {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
        {
          allMarcheToValidateGef(filters: {
            siteIds: [${idsSites}]
          }) {
              id,
              libelle,
              site {
                id,
                libelle
              }
          }
        }
      `)
  }

  findLotMarcheToValidate(): Observable<any> {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    return this.graphQlSvc.sendQuery(`
        {
          allLotMarcheToValidateGef(filters: {
            siteIds: [${idsSites}]
          }) {
              id,
              libelle,
              site {
                id,
                libelle
              },
              marche{
                site {
                  id,
                  libelle
                },
              }
          }
        }
      `)
  }

}

export interface GefConnectionSavedSupplier {
  isUpdate: boolean;
  gefConnection: GefConnectionDto;
}

export interface GefDuplicationDialogSupplier {
  connection: GefConnectionDto;
  existingConnections: GefConnectionDto[];
}
